import { CpuIcon, SearchIcon } from "lucide-react";
import type { AccessMode, TurnUsage } from "../../../../shared/api.ts";
import { TooltipContent, TooltipRoot, TooltipTrigger } from "@/components/ui/tooltip.tsx";
import { useI18n } from "@/i18n/index.tsx";
import { writesUnreported } from "@/lib/turns.ts";
import { cn } from "@/lib/utils.ts";

/**
 * Tokens (input / output), web searches, and money. The charge is shown when it is above zero;
 * otherwise the API-equivalent cost appears only in the tooltip, labelled as an estimate when it is one.
 */
export function UsageChip({
  usage,
  accessMode,
  model,
  className,
}: {
  usage: TurnUsage;
  accessMode: AccessMode | null;
  model: string | null;
  className?: string;
}) {
  const { t, fmt } = useI18n();
  const charged = usage.chargeUsd > 0;
  const unreported = writesUnreported(accessMode);
  const rows: Array<[string, string]> = [
    [t.turn.usage.input, fmt.number(usage.inputTokens)],
    [t.turn.usage.cached, fmt.number(usage.cachedInputTokens)],
    [t.turn.usage.cacheWrite, unreported ? t.turn.usage.notReported : fmt.number(usage.cacheWriteInputTokens)],
    [t.turn.usage.output, fmt.number(usage.outputTokens)],
    [t.turn.usage.reasoning, fmt.number(usage.reasoningTokens)],
    [t.turn.usage.searches, fmt.number(usage.webSearches)],
    [usage.costEstimated ? t.turn.usage.apiEquivalentEstimate : t.turn.usage.apiEquivalent, `${usage.costEstimated ? "≈ " : ""}${fmt.usd(usage.costUsd, { precise: true })}`],
  ];
  const notes = usage.costEstimated
    ? [unreported ? t.turn.usage.estimateWrites : null, usage.webSearches > 0 ? t.turn.usage.estimateSearches : null].filter((note): note is string => note !== null)
    : [];
  if (charged) rows.push([t.turn.usage.charged, fmt.usd(usage.chargeUsd, { precise: true })]);
  if (accessMode) rows.push([t.turn.usage.source, t.settings.modes[accessMode]]);
  if (model) rows.push([t.turn.usage.model, model]);

  return (
    <TooltipRoot>
      <TooltipTrigger asChild>
        <button
          type="button"
          aria-label={t.turn.usage.label}
          className={cn(
            "inline-flex h-7 max-w-full items-center gap-2 rounded-full border bg-muted/60 px-2.5 text-[11.5px] text-muted-foreground transition-colors outline-none hover:bg-accent hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-ring/35",
            className,
          )}
        >
          <span className="inline-flex items-center gap-1">
            <CpuIcon className="size-3.5" aria-hidden />
            <span className="code-mono">
              {fmt.compact(usage.inputTokens)} / {fmt.compact(usage.outputTokens)}
            </span>
          </span>
          <span className="inline-flex items-center gap-1">
            <SearchIcon className="size-3.5" aria-hidden />
            <span className="code-mono">{fmt.number(usage.webSearches)}</span>
          </span>
          {charged ? <span className="code-mono font-semibold text-foreground">{fmt.usd(usage.chargeUsd)}</span> : null}
        </button>
      </TooltipTrigger>
      <TooltipContent side="bottom" align="end" className="w-72 max-w-[calc(100vw-2rem)] p-3">
        <p className="mb-2 text-[12px] font-semibold">{t.turn.usage.label}</p>
        <dl className="grid grid-cols-[1fr_auto] gap-x-4 gap-y-1 text-[12px]">
          {rows.map(([label, value]) => (
            <div key={label} className="contents">
              <dt className="opacity-75">{label}</dt>
              <dd className="code-mono text-right">{value}</dd>
            </div>
          ))}
        </dl>
        {notes.map((note) => (
          <p key={note} className="mt-2 text-[11.5px] leading-snug opacity-75">
            {note}
          </p>
        ))}
      </TooltipContent>
    </TooltipRoot>
  );
}
